import React from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import RevealOnScroll from "../components/RevealOnScroll";
import { BOTANICAL_SRC } from "../components/BotanicalOverlay";

/* =====================================================================
   Palette tokens — Reflection Deck page leans into Alice-inspired pinks.
   Pulled back on the blues/teals previously used here.
   ===================================================================== */
const C = {
    cream: "#FBF6F1",
    ivory: "#FFFCF8",
    // Palette overhaul (iter_15): removes terracotta / warm salmon / yellow-gold entirely.
    // Aliases (teal / lilac / midnight / rose / blush / gold / sage) are kept so existing
    // component references keep working — they now resolve to the new palette values.
    berry: "#7A2D50",       // deep reddish-purple — primary CTA color (all "Book a Reading" buttons)
    tealDeep: "#5C7A78",    // dusty muted teal — decorative + secondary
    rosePurple: "#A88CA0",  // soft lavender-leaning mauve — accents + subtle borders

    midnight: "#7A2D50",    // was oxblood terracotta — now berry
    lilac: "#7A2D50",       // was oxblood terracotta — now berry (unified with midnight)
    teal: "#5C7A78",        // was a tea-rose pink misnomer — now genuine dusty teal
    sage: "#7C9793",        // slightly lighter dusty teal, decorative
    rose: "#A88CA0",        // was dusty rose — now dusty rose-purple (mauve)
    blush: "#EAD8E2",       // very soft rose-purple tint for backgrounds
    gold: "#A88CA0",        // legacy alias — mapped to dusty rose-purple so gold references are neutralized
    charcoal: "#2C2825",
    taupe: "#7C6E7A",       // was warm taupe — now cool taupe with mauve undertone
};

// Unified "Book a Reading" button color (all sticky/inline/ready-to-begin instances on
// Reflection Deck AND Extended Path use this exact color per iter_15 spec).
const BOOK_BTN_COLOR = "#7A2D50";

const HERO_TEA_PARTY_IMG =
    "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/lav8jupt_ChatGPT%20Image%20Jul%207%2C%202026%20at%2004_18_57%20PM.png";
const CLOSING_DOOR_IMG =
    "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/brqwflr3_ChatGPT%20Image%20Jun%2024%2C%202026%20at%2010_12_17%20PM%20%282%29.png";
const BIDDY_BADGE_URL =
    "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/u853whiv_CBTR-Badge-Dark.png";

/* =====================================================================
   Tarot suit icons — minimal single-line SVG
   ===================================================================== */
const Cups = ({ size = 16, color = "currentColor", opacity = 1, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ opacity, ...style }} aria-hidden>
        <path d="M5 5 H19 V10 A7 7 0 0 1 5 10 Z M12 17 V21 M8 21 H16" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const Wands = ({ size = 16, color = "currentColor", opacity = 1, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ opacity, ...style }} aria-hidden>
        <path d="M12 3 V22 M12 6 C10 5.5 8.5 4.5 8 3 C10 3 11 4 12 6 Z" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const Swords = ({ size = 16, color = "currentColor", opacity = 1, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ opacity, ...style }} aria-hidden>
        <path d="M12 2 L14 17 L12 22 L10 17 Z M7 17 H17" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const Pentacles = ({ size = 16, color = "currentColor", opacity = 1, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ opacity, ...style }} aria-hidden>
        <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.2" />
        <path d="M12 5 L13.6 10 L19 10 L14.7 13.1 L16.3 18 L12 14.9 L7.7 18 L9.3 13.1 L5 10 L10.4 10 Z" stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
);

const SUITS = { Cups, Wands, Swords, Pentacles };

/* =====================================================================
   Wallpaper pattern — 80×80 tile, 4 suit glyphs in 2×2 grid
   Built as an inline SVG data URI; opacity controlled by caller via wrapper.
   ===================================================================== */
const buildWallpaperUrl = (color = "#2C2825") => {
    const enc = encodeURIComponent(color);
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'>
  <g fill='none' stroke='${enc}' stroke-width='1.1' stroke-linecap='round' stroke-linejoin='round'>
    <!-- Cup top-left, cell 0–40 -->
    <path d='M14 10 H26 V16 A6 6 0 0 1 14 16 Z M20 22 V26 M17 26 H23'/>
    <!-- Wand top-right, cell 40–80 -->
    <path d='M60 8 V28 M60 11 C58.5 10.6 57.5 9.8 57 8.5 C58.5 8.7 59.3 9.5 60 11 Z'/>
    <!-- Sword bottom-left -->
    <path d='M20 44 L21.4 56 L20 60 L18.6 56 Z M16 56 H24'/>
    <!-- Pentacle bottom-right -->
    <circle cx='60' cy='54' r='8'/>
    <path d='M60 48 L61.2 52 L65 52 L62 54.4 L63.2 58 L60 55.8 L56.8 58 L58 54.4 L55 52 L58.8 52 Z'/>
  </g>
</svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

const Wallpaper = ({ color = C.charcoal, opacity = 0.04 }) => (
    <div
        aria-hidden
        style={{
            position: "absolute",
            inset: 0,
            backgroundImage: buildWallpaperUrl(color),
            backgroundRepeat: "repeat",
            backgroundSize: "80px 80px",
            opacity,
            pointerEvents: "none",
            zIndex: 0,
        }}
    />
);

/* =====================================================================
   Botanical flourishes (top-right + bottom-left) — uses existing site asset
   ===================================================================== */
const Botanical = ({ corner = "tr", opacity = 0.13, invert = false }) => {
    const base = {
        position: "absolute",
        width: "55%",
        height: "70%",
        backgroundImage: `url(${BOTANICAL_SRC})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        mixBlendMode: invert ? "screen" : "multiply",
        filter: invert ? "invert(1) brightness(1.05)" : "none",
        opacity,
        pointerEvents: "none",
        zIndex: 0,
        WebkitMaskImage: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,1) 100%)",
        maskImage: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,1) 100%)",
    };
    if (corner === "tr") return <div aria-hidden style={{ ...base, top: "-10%", right: "-10%", backgroundPosition: "right top" }} />;
    return <div aria-hidden style={{ ...base, bottom: "-10%", left: "-10%", backgroundPosition: "left bottom" }} />;
};

/* =====================================================================
   Corner frame ornaments — L-shaped lines in 4 corners
   ===================================================================== */
const CornerOrnaments = ({ color = C.gold, opacity = 0.2, inset = 16, length = 22, weight = 1 }) => {
    const arm = { position: "absolute", background: color, opacity };
    const h = { ...arm, height: weight, width: length };
    const v = { ...arm, width: weight, height: length };
    return (
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
            {/* TL */}
            <div style={{ ...h, top: inset, left: inset }} />
            <div style={{ ...v, top: inset, left: inset }} />
            {/* TR */}
            <div style={{ ...h, top: inset, right: inset }} />
            <div style={{ ...v, top: inset, right: inset }} />
            {/* BL */}
            <div style={{ ...h, bottom: inset, left: inset }} />
            <div style={{ ...v, bottom: inset, left: inset }} />
            {/* BR */}
            <div style={{ ...h, bottom: inset, right: inset }} />
            <div style={{ ...v, bottom: inset, right: inset }} />
        </div>
    );
};

/* =====================================================================
   Ornate Victorian divider — ♦ ✦ ♦ between thin gold rules
   ===================================================================== */
const OrnateDivider = ({ bg = C.cream }) => (
    <div style={{ background: bg, padding: "0 16px" }} aria-hidden>
        <div
            className="mx-auto"
            style={{
                maxWidth: 720,
                display: "flex",
                alignItems: "center",
                gap: 18,
                padding: "32px 0",
            }}
        >
            <span style={{ flex: 1, height: 1, background: C.gold, opacity: 0.4 }} />
            <span style={{ color: C.gold, opacity: 0.7, fontSize: 12, letterSpacing: "0.4em" }}>♦ ✦ ♦</span>
            <span style={{ flex: 1, height: 1, background: C.gold, opacity: 0.4 }} />
        </div>
    </div>
);

/* =====================================================================
   Gothic arched door — detailed SVG line art
   ===================================================================== */
const GothicDoor = ({ width = 80, height = 110, color = C.midnight, opacity = 0.22, style = {} }) => (
    <svg
        width={width}
        height={height}
        viewBox="0 0 100 140"
        fill="none"
        aria-hidden
        style={{ opacity, display: "block", ...style }}
    >
        <g stroke={color} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
            {/* climbing vines on the frame */}
            <path d="M8 70 C 12 60 9 50 14 42 M14 42 C 16 38 13 33 17 28 M14 42 C 17 44 19 42 20 39" opacity="0.55" />
            <path d="M92 70 C 88 60 91 50 86 42 M86 42 C 84 38 87 33 83 28 M86 42 C 83 44 81 42 80 39" opacity="0.55" />
            {/* outer stone arch — pointed Gothic */}
            <path d="M14 50 L14 130 L86 130 L86 50 C 86 30 70 14 50 14 C 30 14 14 30 14 50 Z" />
            {/* stone block segments along the arch */}
            <path d="M14 50 L20 47" />
            <path d="M16 38 L22 38" />
            <path d="M22 28 L28 31" />
            <path d="M32 20 L36 25" />
            <path d="M50 14 L50 22" />
            <path d="M68 20 L64 25" />
            <path d="M78 28 L72 31" />
            <path d="M84 38 L78 38" />
            <path d="M86 50 L80 47" />
            {/* inner arch / door opening */}
            <path d="M22 56 L22 124 L78 124 L78 56 C 78 38 65 24 50 24 C 35 24 22 38 22 56 Z" />
            {/* trefoil motifs in the tympanum */}
            <circle cx="50" cy="38" r="3" />
            <circle cx="42" cy="46" r="2.4" />
            <circle cx="58" cy="46" r="2.4" />
            {/* central dividing line — two-leaf door */}
            <path d="M50 56 L50 124" />
            {/* four raised panels — 2×2 (two per leaf) */}
            <path d="M28 64 L46 64 L46 88 L28 88 Z" />
            <path d="M28 92 L46 92 L46 118 L28 118 Z" />
            <path d="M54 64 L72 64 L72 88 L54 88 Z" />
            <path d="M54 92 L72 92 L72 118 L54 118 Z" />
            {/* iron strap hinges crossing each panel horizontally */}
            <path d="M24 70 L46 70 M24 84 L46 84" />
            <path d="M24 100 L46 100 M24 114 L46 114" />
            <path d="M54 70 L76 70 M54 84 L76 84" />
            <path d="M54 100 L76 100 M54 114 L76 114" />
            {/* strap hinge ends — fishtails */}
            <path d="M24 70 L21 67 L24 70 L21 73" />
            <path d="M24 100 L21 97 L24 100 L21 103" />
            {/* iron ring knocker on right panel */}
            <circle cx="63" cy="76" r="4" />
            <path d="M63 72 L63 70" />
            {/* keyhole escutcheon below knocker */}
            <path d="M61 86 L65 86 L65 96 L61 96 Z" />
            <circle cx="63" cy="89" r="1" fill={color} stroke="none" opacity="0.7" />
            <path d="M63 90 L63 94" />
            {/* stone threshold with molding */}
            <path d="M10 130 L90 130 L90 134 L10 134 Z" />
            <path d="M8 134 L92 134 L92 138 L8 138 Z" />
        </g>
    </svg>
);

/* =====================================================================
   Reusable bits
   ===================================================================== */
const Eyebrow = ({ children, color = C.teal, withLines = false, lineColor }) => {
    const lc = lineColor || color;
    return (
        <div className="flex items-center justify-center gap-3">
            {withLines && <span style={{ height: 1, width: 38, background: lc, opacity: 0.4 }} />}
            <span
                style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 11,
                    letterSpacing: "0.36em",
                    textTransform: "uppercase",
                    color,
                    fontWeight: 500,
                }}
            >
                {children}
            </span>
            {withLines && <span style={{ height: 1, width: 38, background: lc, opacity: 0.4 }} />}
        </div>
    );
};

const LilacQuote = ({ children, style = {} }) => (
    <div
        style={{
            position: "relative",
            background: C.lilac,
            padding: "30px 34px",
            color: C.cream,
            ...style,
        }}
    >
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 5,
                border: "1px solid rgba(245,240,232,0.2)",
                pointerEvents: "none",
            }}
        />
        <p
            style={{
                position: "relative",
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                fontSize: 20,
                lineHeight: 1.55,
                margin: 0,
            }}
        >
            {children}
        </p>
    </div>
);

/* Inline tarot-cards illustration — refined, no candles/crystals/people */
const TarotCardsArrangement = ({ height = 320 }) => (
    <svg
        viewBox="0 0 640 420"
        width="100%"
        height={height}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
        style={{ display: "block" }}
    >
        <defs>
            <linearGradient id="rdLinen" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F8EDE8" />
                <stop offset="100%" stopColor="#EFD9DC" />
            </linearGradient>
            <linearGradient id="rdCard" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFFCF8" />
                <stop offset="100%" stopColor="#F7EDE4" />
            </linearGradient>
            <linearGradient id="rdCardBack" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#9C3550" />
                <stop offset="100%" stopColor="#7D2F3F" />
            </linearGradient>
            <filter id="rdShadow" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                <feOffset dx="0" dy="2" result="off" />
                <feComponentTransfer><feFuncA type="linear" slope="0.18"/></feComponentTransfer>
                <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
        </defs>

        {/* linen tablecloth */}
        <rect x="0" y="0" width="640" height="420" fill="url(#rdLinen)" />
        {/* table edge shadow */}
        <ellipse cx="320" cy="395" rx="280" ry="14" fill="#000" opacity="0.06"/>

        {/* Card backs in a small fan on the left (refined Rider-Waite back style) */}
        <g filter="url(#rdShadow)" transform="translate(72, 64)">
            {[-14, -7, 0].map((rot, i) => (
                <g key={i} transform={`rotate(${rot} 60 130)`}>
                    <rect x="0" y="0" width="120" height="200" rx="6" fill="url(#rdCardBack)" stroke="#5A1F2C" strokeWidth="1"/>
                    <rect x="6" y="6" width="108" height="188" rx="4" fill="none" stroke="#D3C187" strokeWidth="0.8" opacity="0.6"/>
                    {/* central rose motif */}
                    <circle cx="60" cy="100" r="22" stroke="#D3C187" strokeWidth="1" fill="none" opacity="0.7"/>
                    <circle cx="60" cy="100" r="12" stroke="#D3C187" strokeWidth="0.8" fill="none" opacity="0.7"/>
                    <circle cx="60" cy="100" r="4" fill="#D3C187" opacity="0.7"/>
                    {/* corner stars */}
                    <text x="12" y="22" fontSize="10" fill="#D3C187" opacity="0.7" fontFamily="serif">✦</text>
                    <text x="100" y="194" fontSize="10" fill="#D3C187" opacity="0.7" fontFamily="serif">✦</text>
                </g>
            ))}
        </g>

        {/* Three face-up cards laid in a row (simplified Major Arcana style) */}
        {[
            { x: 260, label: "I", title: "The Magician", glyph: "M" },
            { x: 400, label: "II", title: "The Priestess", glyph: "II" },
            { x: 540, label: "III", title: "The Empress", glyph: "III" },
        ].map((card, i) => (
            <g key={i} filter="url(#rdShadow)" transform={`translate(${card.x - 60}, ${100 + (i === 1 ? -10 : 0)})`}>
                <rect x="0" y="0" width="120" height="200" rx="6" fill="url(#rdCard)" stroke="#C9B996" strokeWidth="0.8"/>
                <rect x="8" y="8" width="104" height="184" rx="3" fill="none" stroke="#C68FA2" strokeWidth="0.7" opacity="0.7"/>
                {/* roman numeral top */}
                <text x="60" y="34" fontSize="14" fill="#7D2F3F" textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontStyle="italic">
                    {card.label}
                </text>
                {/* central glyph circle */}
                <circle cx="60" cy="100" r="34" stroke="#C68FA2" strokeWidth="0.8" fill="#F4D6DE" opacity="0.6"/>
                <text x="60" y="108" fontSize="26" fill="#7D2F3F" textAnchor="middle" fontFamily="'Cormorant Garamond', serif">
                    {card.glyph}
                </text>
                {/* card title at bottom */}
                <text x="60" y="180" fontSize="9" fill="#7D2F3F" textAnchor="middle" fontFamily="'DM Sans', sans-serif" letterSpacing="1.5">
                    {card.title.toUpperCase()}
                </text>
                {/* corner ornaments */}
                <text x="12" y="22" fontSize="8" fill="#D3C187" fontFamily="serif">✦</text>
                <text x="108" y="194" fontSize="8" fill="#D3C187" fontFamily="serif">✦</text>
            </g>
        ))}
    </svg>
);

/* Inline tea-party illustration — teapot + teacup + 2 tarot cards on a set table */
const TeaPartyArrangement = ({ height = 360 }) => (
    <svg
        viewBox="0 0 720 420"
        width="100%"
        height={height}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
        style={{ display: "block" }}
    >
        <defs>
            <linearGradient id="tpCloth" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FAEDEE" />
                <stop offset="100%" stopColor="#F0D7DD" />
            </linearGradient>
            <linearGradient id="tpPorc" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="100%" stopColor="#F4E5E7" />
            </linearGradient>
            <linearGradient id="tpCard" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFFCF8" />
                <stop offset="100%" stopColor="#F7EDE4" />
            </linearGradient>
            <filter id="tpShadow" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="2.5"/>
                <feOffset dx="0" dy="3" result="off"/>
                <feComponentTransfer><feFuncA type="linear" slope="0.2"/></feComponentTransfer>
                <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
        </defs>

        {/* tablecloth */}
        <rect x="0" y="0" width="720" height="420" fill="url(#tpCloth)"/>
        {/* lace edge at bottom */}
        <path d="M0 400 Q 30 388, 60 400 T 120 400 T 180 400 T 240 400 T 300 400 T 360 400 T 420 400 T 480 400 T 540 400 T 600 400 T 660 400 T 720 400 L 720 420 L 0 420 Z" fill="#E8C6CE" opacity="0.55"/>

        {/* Saucer + teacup */}
        <g filter="url(#tpShadow)" transform="translate(150, 200)">
            {/* saucer */}
            <ellipse cx="80" cy="80" rx="76" ry="18" fill="url(#tpPorc)" stroke="#C68FA2" strokeWidth="0.8"/>
            <ellipse cx="80" cy="80" rx="56" ry="12" fill="#F8EDEF" opacity="0.7"/>
            {/* cup base shadow */}
            <ellipse cx="80" cy="64" rx="40" ry="6" fill="#000" opacity="0.06"/>
            {/* cup body */}
            <path d="M44 28 C 44 60, 50 76, 80 76 C 110 76, 116 60, 116 28 Z" fill="url(#tpPorc)" stroke="#C68FA2" strokeWidth="0.9"/>
            {/* tea surface */}
            <ellipse cx="80" cy="28" rx="36" ry="6" fill="#B66D7A" opacity="0.9"/>
            <ellipse cx="80" cy="28" rx="32" ry="4" fill="#A05060" opacity="0.5"/>
            {/* handle */}
            <path d="M116 38 C 132 38, 134 60, 118 62" fill="none" stroke="#C68FA2" strokeWidth="1.4"/>
            {/* rim gold */}
            <ellipse cx="80" cy="28" rx="36" ry="6" fill="none" stroke="#D3C187" strokeWidth="0.7" opacity="0.9"/>
            {/* tiny steam */}
            <path d="M68 14 Q 70 6, 64 -2 Q 60 -8, 66 -14" stroke="#C68FA2" strokeWidth="1" fill="none" opacity="0.6"/>
            <path d="M90 18 Q 96 8, 90 0" stroke="#C68FA2" strokeWidth="1" fill="none" opacity="0.6"/>
        </g>

        {/* Teapot */}
        <g filter="url(#tpShadow)" transform="translate(380, 130)">
            {/* body */}
            <path d="M30 90 C 30 60, 60 38, 110 38 C 160 38, 190 60, 190 90 C 190 130, 160 152, 110 152 C 60 152, 30 130, 30 90 Z" fill="url(#tpPorc)" stroke="#C68FA2" strokeWidth="0.9"/>
            {/* lid */}
            <ellipse cx="110" cy="42" rx="48" ry="10" fill="url(#tpPorc)" stroke="#C68FA2" strokeWidth="0.8"/>
            <ellipse cx="110" cy="42" rx="42" ry="6" fill="#F8EDEF"/>
            {/* lid knob */}
            <circle cx="110" cy="30" r="6" fill="#D3C187" stroke="#A98D4C" strokeWidth="0.6"/>
            {/* spout */}
            <path d="M30 78 C 16 76, 4 74, -2 70 C 6 80, 12 90, 30 96 Z" fill="url(#tpPorc)" stroke="#C68FA2" strokeWidth="0.8"/>
            {/* handle */}
            <path d="M190 76 C 220 70, 226 100, 196 110" fill="none" stroke="#C68FA2" strokeWidth="2.2"/>
            {/* gold floral motif */}
            <g opacity="0.75" stroke="#D3C187" strokeWidth="0.7" fill="none">
                <circle cx="100" cy="95" r="8"/>
                <circle cx="100" cy="95" r="4"/>
                <path d="M88 95 Q 100 80, 112 95"/>
                <path d="M88 95 Q 100 110, 112 95"/>
                <circle cx="130" cy="100" r="2.5" fill="#D3C187"/>
                <circle cx="78" cy="100" r="2.5" fill="#D3C187"/>
            </g>
            {/* spout drip */}
            <path d="M0 70 Q -2 90, 0 100" stroke="#D3C187" strokeWidth="0.6" fill="none" opacity="0.6"/>
        </g>

        {/* Two tarot cards laid beside the saucer */}
        {[
            { x: 70, y: 110, rot: -8, label: "VI", title: "The Lovers" },
            { x: 220, y: 100, rot: 6, label: "XVII", title: "The Star" },
        ].map((card, i) => (
            <g key={i} filter="url(#tpShadow)" transform={`translate(${card.x}, ${card.y}) rotate(${card.rot})`}>
                <rect x="0" y="0" width="84" height="140" rx="5" fill="url(#tpCard)" stroke="#C9B996" strokeWidth="0.7"/>
                <rect x="6" y="6" width="72" height="128" rx="3" fill="none" stroke="#C68FA2" strokeWidth="0.6" opacity="0.7"/>
                <text x="42" y="26" fontSize="11" fill="#7D2F3F" textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontStyle="italic">{card.label}</text>
                <circle cx="42" cy="70" r="22" stroke="#C68FA2" strokeWidth="0.6" fill="#F4D6DE" opacity="0.55"/>
                <text x="42" y="126" fontSize="7" fill="#7D2F3F" textAnchor="middle" fontFamily="'DM Sans', sans-serif" letterSpacing="1">{card.title.toUpperCase()}</text>
            </g>
        ))}

        {/* Tiny rose petals scattered for tea-party feel */}
        {[
            { x: 540, y: 320, r: 4 },
            { x: 310, y: 310, r: 3.5 },
            { x: 600, y: 280, r: 3 },
            { x: 56, y: 360, r: 3.5 },
        ].map((p, i) => (
            <g key={i} transform={`translate(${p.x}, ${p.y})`}>
                <circle r={p.r} fill="#E89BBE" opacity="0.85"/>
                <circle r={p.r * 0.45} fill="#FAF6EB" opacity="0.5"/>
            </g>
        ))}
    </svg>
);

/* =====================================================================
   SECTION 1 — HERO (subcategory-scale, illustrated tarot arrangement)
   ===================================================================== */
const Hero = () => (
    <section
        data-testid="readings-hero"
        className="relative overflow-hidden"
        style={{ background: C.cream }}
    >
        <Wallpaper />
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] relative" style={{ zIndex: 2 }}>
            {/* LEFT — text (subcategory scale) */}
            <div
                className="relative flex items-center"
                style={{ padding: "96px 28px 72px" }}
            >
                <div className="mx-auto" style={{ maxWidth: 520 }} data-reveal>
                    <div className="flex items-center gap-3">
                        <span style={{ flex: "0 0 28px", height: 1, background: C.midnight, opacity: 0.6 }} />
                        <span
                            data-testid="readings-eyebrow"
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 13,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                color: C.midnight,
                                fontWeight: 500,
                            }}
                        >
                            Alchemy Extended, Tarot Enhancements
                        </span>
                    </div>

                    <h1
                        className="mt-6 font-serif"
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontWeight: 500,
                            fontSize: "clamp(30px, 3vw, 42px)",
                            lineHeight: 1.08,
                            color: C.charcoal,
                            letterSpacing: "-0.004em",
                        }}
                    >
                        The Reflection{" "}
                        <em style={{ fontStyle: "italic", color: C.lilac, fontWeight: 500 }}>
                            Deck.
                        </em>
                    </h1>
                    <p
                        className="mt-5"
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 21,
                            color: C.charcoal,
                            opacity: 0.92,
                            lineHeight: 1.55,
                            maxWidth: 520,
                        }}
                    >
                        A different kind of reading — for people who want to go deeper. Pulled
                        slowly, held with care, served the way you would set a table for a
                        guest you actually like.
                    </p>
                    <p
                        className="mt-4"
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 16.5,
                            lineHeight: 1.8,
                            color: C.charcoal,
                            opacity: 0.9,
                            maxWidth: 520,
                        }}
                    >
                        The Reflection Deck brings psychological depth and symbolic richness to
                        the tarot experience. This is not therapy. It is something else
                        entirely — and for the right person, it is exactly what they have been
                        looking for.
                    </p>
                </div>
            </div>

            {/* RIGHT — refined tea-party photograph */}
            <div
                className="relative overflow-hidden flex items-center"
                style={{ background: "#F8EDE8", minHeight: 340 }}
            >
                <img
                    src={HERO_TEA_PARTY_IMG}
                    alt="A sunlit window seat with two upholstered striped chairs, a round linen-draped table set with a patterned teapot, teacup, fresh white florals, biscuits, and tarot cards laid across the cloth"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "saturate(0.96) brightness(1.02)" }}
                />
                <span
                    aria-hidden
                    style={{
                        position: "absolute",
                        top: 18,
                        right: 18,
                        color: C.gold,
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 18,
                        lineHeight: 1,
                    }}
                >
                    ✦
                </span>
            </div>
        </div>
    </section>
);

/* =====================================================================
   SECTION 2 — INTRO SPLIT
   ===================================================================== */
const IntroSplit = () => (
    <section
        data-testid="readings-intro-split"
        className="relative"
        style={{ background: C.cream }}
    >
        <div className="mx-auto" style={{ maxWidth: 780 }}>
            {/* Cream column with wallpaper (duplicate hero image removed) */}
            <div className="relative" style={{ padding: "72px 40px" }}>
                <Wallpaper />
                <div className="relative" style={{ zIndex: 1 }} data-reveal>
                    <h2
                        className="mb-6"
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontWeight: 400,
                            fontSize: "clamp(30px, 3.2vw, 40px)",
                            lineHeight: 1.18,
                            color: C.charcoal,
                            margin: 0,
                        }}
                    >
                        What is this,{" "}
                        <em style={{ fontStyle: "italic", color: C.midnight }}>really?</em>
                    </h2>

                    <p
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 17.5,
                            color: C.charcoal,
                            lineHeight: 1.85,
                            opacity: 0.95,
                            margin: 0,
                        }}
                    >
                        <span
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: 80,
                                lineHeight: "0.85",
                                color: C.teal,
                                opacity: 0.7,
                                float: "left",
                                padding: "8px 12px 0 0",
                            }}
                        >
                            W
                        </span>
                        hatever you bring to the cards, we work with it — honestly, and with your
                        growth in mind.
                    </p>

                    <p
                        className="mt-6"
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 17.5,
                            color: C.charcoal,
                            opacity: 0.92,
                            lineHeight: 1.85,
                        }}
                    >
                        The Reflection Deck is for people who come to the cards with a real question
                        — about love, direction, identity, a decision they are sitting with, or
                        something they are hoping for and do not quite know how to move toward.
                        Whatever you bring, we work with it. If you want to know about a
                        relationship, we look at what the cards are asking of you — what
                        conditions you are being invited to create.
                    </p>

                    {/* Teal-bordered callout */}
                    <div
                        className="mt-8"
                        style={{
                            background: C.ivory,
                            border: "1px solid rgba(123,139,155,0.2)",
                            borderLeft: `3px solid ${C.teal}`,
                            padding: "18px 22px",
                        }}
                    >
                        <p
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontStyle: "italic",
                                fontSize: 20,
                                color: C.charcoal,
                                margin: 0,
                                lineHeight: 1.55,
                            }}
                        >
                            The cards are not the answer. They are the key — to what you already
                            know but have not yet been willing to look at directly.
                        </p>
                    </div>

                    <LilacQuote style={{ marginTop: 24 }}>
                        The cards do not tell you what will happen. They show you what is
                        possible — and what your part in making it possible might be.
                    </LilacQuote>
                </div>
            </div>
        </div>
    </section>
);

/* =====================================================================
   SECTION 3 — BADGE (right-aligned, with explanation)
   ===================================================================== */
const Badge = () => (
    <section
        data-testid="readings-badge"
        style={{
            background: C.ivory,
            padding: "56px 24px",
            borderTop: "1px solid rgba(198,143,162,0.28)",
            borderBottom: "1px solid rgba(198,143,162,0.28)",
        }}
    >
        <div className="mx-auto flex flex-col sm:flex-row items-center sm:justify-end gap-7" style={{ maxWidth: 1180 }}>
            <div className="sm:text-right" style={{ maxWidth: 520 }}>
                <p
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 17,
                        lineHeight: 1.55,
                        color: C.charcoal,
                        margin: 0,
                    }}
                >
                    Biddy Tarot&apos;s certification is the most widely recognized standard for
                    professional tarot readers — a structured program that combines deep card
                    knowledge with ethical practice, trauma-aware reading, and a code of conduct.
                    It means the cards are being held by someone who has been trained, tested,
                    and held accountable to a real standard.
                </p>
                <p
                    style={{
                        marginTop: 14,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: C.midnight,
                        fontWeight: 500,
                    }}
                >
                    Biddy Tarot · Certified Reader
                </p>
            </div>
            <img
                src={BIDDY_BADGE_URL}
                alt="Biddy Tarot Certified Reader badge"
                data-testid="biddy-badge-image"
                style={{
                    width: 130,
                    height: 130,
                    objectFit: "contain",
                    flexShrink: 0,
                }}
            />
        </div>
    </section>
);

/* =====================================================================
   SECTION 5 — OFFERINGS
   ===================================================================== */
const OFFERINGS = [
    {
        eyebrow: "Primary · Email",
        title: "The Email Reading",
        body: "The main way clients begin. You submit your question. I pull the cards and deliver a written reflection — the meanings, what they ask of you, and what to carry forward. Delivered within five business days.",
        suit: "Wands",
        href: "/book-a-reading",
        cta: "Begin a Reading",
    },
];

const OfferingCard = ({ o }) => {
    const Suit = SUITS[o.suit];
    return (
        <article
            data-testid={`readings-offering-${o.suit.toLowerCase()}`}
            className="relative"
            style={{
                background: C.ivory,
                border: "1px solid rgba(123,139,155,0.2)",
                padding: "36px 30px 28px",
                minHeight: 360,
            }}
        >
            {/* top gradient accent */}
            <div
                aria-hidden
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, ${C.teal}, ${C.lilac})`,
                    opacity: 0.45,
                }}
            />
            <CornerOrnaments color={C.gold} opacity={0.3} inset={10} length={14} />

            {/* ghost suit icon */}
            <div
                aria-hidden
                style={{ position: "absolute", right: 16, bottom: 16, opacity: 0.07 }}
            >
                <Suit size={30} color={C.charcoal} />
            </div>

            <div className="relative" style={{ zIndex: 1 }}>
                <p
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: C.teal,
                        fontWeight: 500,
                    }}
                >
                    {o.eyebrow}
                </p>
                <h3
                    className="mt-3"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 24,
                        color: C.charcoal,
                        fontWeight: 500,
                    }}
                >
                    {o.title}
                </h3>
                <p
                    className="mt-4"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 17,
                        color: C.charcoal,
                        opacity: 0.9,
                        lineHeight: 1.75,
                    }}
                >
                    {o.body}
                </p>
                <a
                    href={o.href}
                    data-testid={`readings-offering-cta-${o.suit.toLowerCase()}`}
                    className="mt-8 pt-4 inline-flex items-center gap-2"
                    style={{
                        borderTop: "1px solid rgba(198,143,162,0.35)",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        color: C.midnight,
                        fontWeight: 500,
                    }}
                >
                    {o.cta}
                    <span aria-hidden style={{ transform: "translateY(-1px)" }}>→</span>
                </a>
            </div>
        </article>
    );
};

const Offerings = () => (
    <section
        data-testid="readings-offerings"
        className="relative overflow-hidden"
        style={{ background: C.cream, padding: "80px 24px 96px" }}
    >
        <Wallpaper />
        <div className="relative mx-auto" style={{ maxWidth: 1080, zIndex: 1 }}>
            <div className="text-center" data-reveal>
                <Eyebrow color={C.lilac}>How we can work together</Eyebrow>
                <h2
                    className="mt-4"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 36,
                        color: C.charcoal,
                        fontWeight: 400,
                    }}
                >
                    How to <em style={{ fontStyle: "italic", color: C.lilac }}>begin.</em>
                </h2>
                <p
                    className="mt-3 mx-auto"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 20,
                        color: C.charcoal,
                        opacity: 0.85,
                        lineHeight: 1.55,
                        maxWidth: 680,
                    }}
                >
                    The Reflection Deck is offered as a written email reading —
                    pulled slowly, held with care, and written with your specific question in mind.
                </p>
            </div>

            <div className="mt-14 flex justify-center" data-reveal>
                <div className="w-full" style={{ maxWidth: 560 }}>
                    {OFFERINGS.map((o) => <OfferingCard key={o.title} o={o} />)}
                </div>
            </div>
        </div>
    </section>
);

/* =====================================================================
   SECTION 6 — FAQ
   ===================================================================== */
const FAQS = [
    { suit: "Cups", q: "What kind of questions can I bring?", a: "Anything that is genuinely on your mind. Relationships, career decisions, a transition you are navigating, something you are hoping for. There is no question too big or too ordinary. If it matters to you, it is worth bringing." },
    { suit: "Swords", q: "Will you tell me what is going to happen?", a: "No — and honestly, that is not what makes a reading useful. What I will do is look at what the cards are asking of you in relation to your question. The future is not fixed. What you do next has everything to do with what that future looks like." },
    { suit: "Wands", q: "I want to know if my ex is coming back. Can we work with that?", a: "Yes — just not in the way you might expect. Rather than looking at what your ex is going to do, we look at what the cards are asking of you in that situation. What conditions you are being invited to create. That is a much more empowering place to work from." },
    { suit: "Pentacles", q: "Do I need to believe in tarot for this to be meaningful?", a: "No. You need a willingness to reflect honestly and a real question you care about. Whether you experience this as something mystical or simply as a very good thinking tool — either way, something tends to move." },
    { suit: "Cups", q: "Can I book if I live outside Wisconsin and Colorado?", a: "Yes — Alchemy Readings have no geographic restriction. My therapy practice is limited to Wisconsin and Colorado. Readings welcome clients from anywhere in the world." },
    { suit: "Swords", q: "What happens if something heavy comes up?", a: "I will hold it with care. If something feels like it needs clinical support I will say so clearly and let you know that therapy may be worth exploring. You will never be left without a next step." },
];

const FAQRow = ({ item, last }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <div
            className="py-2"
            style={{ borderBottom: last ? "none" : "1px solid rgba(123,139,155,0.18)" }}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`faq-toggle-${item.q.slice(0, 20).replace(/\s/g, "-")}`}
                className="w-full text-left flex items-center justify-between gap-4"
                style={{
                    background: "transparent",
                    border: "none",
                    padding: "18px 0",
                    cursor: "pointer",
                }}
            >
                <h3
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 19,
                        color: C.midnight,
                        margin: 0,
                        lineHeight: 1.4,
                        fontWeight: 500,
                    }}
                >
                    {item.q}
                </h3>
                <span
                    aria-hidden
                    style={{
                        color: C.midnight,
                        fontSize: 18,
                        fontFamily: "'Cormorant Garamond', serif",
                        flexShrink: 0,
                        transform: open ? "rotate(45deg)" : "rotate(0)",
                        transition: "transform 240ms ease",
                    }}
                >
                    +
                </span>
            </button>
            {open && (
                <p
                    style={{
                        paddingBottom: 18,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 16.5,
                        color: C.charcoal,
                        opacity: 0.92,
                        lineHeight: 1.85,
                        margin: 0,
                    }}
                >
                    {item.a}
                </p>
            )}
        </div>
    );
};

const FAQ = () => (
    <section
        data-testid="readings-faq"
        className="relative overflow-hidden"
        style={{ background: C.ivory, padding: "80px 24px 96px" }}
    >
        {/* subtle botanical top right */}
        <div
            aria-hidden
            style={{
                position: "absolute",
                top: "-8%",
                right: "-8%",
                width: "45%",
                height: "55%",
                backgroundImage: `url(${BOTANICAL_SRC})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "right top",
                mixBlendMode: "multiply",
                opacity: 0.06,
                pointerEvents: "none",
                zIndex: 0,
                WebkitMaskImage: "radial-gradient(ellipse at top right, transparent 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,1) 100%)",
                maskImage: "radial-gradient(ellipse at top right, transparent 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,1) 100%)",
            }}
        />
        <div className="relative mx-auto" style={{ maxWidth: 720, zIndex: 1 }}>
            <div className="text-center" data-reveal>
                <Eyebrow>Your Questions</Eyebrow>
                <h2
                    className="mt-4"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 26,
                        color: C.charcoal,
                        fontWeight: 400,
                    }}
                >
                    What people ask <em style={{ fontStyle: "italic", color: C.lilac }}>before booking.</em>
                </h2>
                <p
                    className="mt-3"
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 17,
                        color: C.charcoal,
                        opacity: 0.75,
                    }}
                >
                    A few things worth knowing before you begin.
                </p>
            </div>

            <div className="mt-12 grid lg:grid-cols-[1fr_320px] gap-10 items-start" data-reveal>
                <div>
                    {FAQS.map((f, i) => (
                        <FAQRow key={i} item={f} last={i === FAQS.length - 1} />
                    ))}
                </div>
                <aside
                    data-testid="readings-faq-aside-quote"
                    className="hidden lg:block"
                    style={{
                        padding: "32px 28px",
                        background: C.blush,
                        borderLeft: `3px solid ${C.midnight}`,
                    }}
                >
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 19,
                            lineHeight: 1.55,
                            color: C.charcoal,
                        }}
                    >
                        Sometimes the most important question is not the one you came with — but
                        the one that surfaces when you finally sit still long enough to listen.
                    </p>
                </aside>
            </div>
        </div>
    </section>
);

/* =====================================================================
   SECTION 7 — DISCLAIMER
   ===================================================================== */
const Disclaimer = () => (
    <section
        data-testid="readings-disclaimer"
        className="relative overflow-hidden"
        style={{ background: C.cream, padding: "72px 24px" }}
    >
        <Wallpaper />
        <div className="relative mx-auto" style={{ maxWidth: 680, zIndex: 1 }} data-reveal>
            <div
                className="relative"
                style={{
                    background: "rgba(42,56,83,0.04)",
                    border: "1px solid rgba(42,56,83,0.1)",
                    padding: "36px 32px",
                }}
            >
                <CornerOrnaments color={C.midnight} opacity={0.12} inset={10} length={14} />
                <p
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 12.5,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: "rgba(42,56,83,0.75)",
                        margin: 0,
                        marginBottom: 8,
                        fontWeight: 600,
                    }}
                >
                    Please read before booking
                </p>
                <p
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 15.5,
                        color: C.charcoal,
                        opacity: 0.88,
                        lineHeight: 1.75,
                        margin: "14px 0 0",
                    }}
                >
                    Alchemy Readings is a tarot reading service offered by Austine Roney in her
                    capacity as a certified tarot reader — not as a licensed mental health
                    professional. Readings are not therapy, counseling, diagnosis, or mental
                    health treatment and do not constitute a therapeutic relationship.
                </p>
                <p
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 15.5,
                        color: C.charcoal,
                        opacity: 0.88,
                        lineHeight: 1.75,
                        margin: "14px 0 0",
                    }}
                >
                    Alchemy Readings and TAM therapy are not offered concurrently. By scheduling a
                    reading you are confirming that you are not currently engaged in a therapeutic
                    relationship with Austine Roney at Alchemy Practice. Alchemy Readings are
                    available to anyone regardless of location.
                </p>
            </div>
        </div>
    </section>
);

/* =====================================================================
   SECTION 7.5 — READY TO BEGIN (booking CTAs — sits before pink closing)
   ===================================================================== */
const ReadyToBegin = () => (
    <section
        id="readings-ready-to-begin"
        data-testid="readings-ready-to-begin"
        className="relative overflow-hidden"
        style={{ background: C.ivory, padding: "80px 24px 96px" }}
    >
        <Wallpaper opacity={0.03} />
        <div className="relative mx-auto text-center" style={{ maxWidth: 820, zIndex: 1 }} data-reveal>
            <Eyebrow color={C.lilac} withLines>Book a Reading</Eyebrow>
            <h2
                className="mt-5"
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "clamp(38px, 4.5vw, 58px)",
                    color: C.charcoal,
                    fontWeight: 400,
                    lineHeight: 1.1,
                }}
            >
                Ready to <em style={{ fontStyle: "italic", color: C.lilac }}>Begin?</em>
            </h2>
            <p
                className="mt-6 mx-auto"
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: 20,
                    color: C.charcoal,
                    opacity: 0.82,
                    lineHeight: 1.55,
                    maxWidth: 620,
                }}
            >
                Choose the reading that meets you where you are.
            </p>
            <div className="mt-12" data-testid="readings-ready-to-begin-cta-row">
                <a
                    href="/book-a-reading"
                    data-testid="readings-book-a-reading-cta"
                    className="inline-flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-90"
                    style={{
                        background: BOOK_BTN_COLOR,
                        color: "#FAFAF8",
                        padding: "20px 44px",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        fontWeight: 500,
                    }}
                >
                    Book a Reading <span aria-hidden>→</span>
                </a>
            </div>
        </div>
    </section>
);

/* =====================================================================
   SECTION 8 — CTA
   ===================================================================== */
const CornerSuit = ({ pos, Suit }) => {
    const map = {
        tl: { top: 28, left: 28 },
        tr: { top: 28, right: 28 },
        bl: { bottom: 28, left: 28 },
        br: { bottom: 28, right: 28 },
    };
    return (
        <div aria-hidden style={{ position: "absolute", ...map[pos], opacity: 0.12, zIndex: 1 }}>
            <Suit size={20} color={C.cream} />
        </div>
    );
};

const CTA = () => (
    <section
        data-testid="readings-cta"
        className="relative overflow-hidden text-center"
        style={{ background: C.teal, padding: "96px 24px" }}
    >
        <Wallpaper color={C.cream} opacity={0.04} />
        <Botanical corner="tr" opacity={0.07} invert />
        <Botanical corner="bl" opacity={0.07} invert />

        {/* outer frame */}
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 16,
                border: "1px solid rgba(211,193,135,0.15)",
                pointerEvents: "none",
                zIndex: 1,
            }}
        />
        {/* inner frame */}
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 32,
                border: "1px solid rgba(211,193,135,0.07)",
                pointerEvents: "none",
                zIndex: 1,
            }}
        />

        <CornerSuit pos="tl" Suit={Cups} />
        <CornerSuit pos="tr" Suit={Wands} />
        <CornerSuit pos="bl" Suit={Swords} />
        <CornerSuit pos="br" Suit={Pentacles} />

        <div className="relative mx-auto" style={{ maxWidth: 540, zIndex: 2 }} data-reveal>
            <div className="flex justify-center">
                <img
                    src={CLOSING_DOOR_IMG}
                    alt="An intricate sepia line-art illustration of an open arched door framed in florals and scrollwork, with a crescent moon and path visible beyond and a small key beneath"
                    data-testid="readings-closing-door"
                    style={{
                        width: 220,
                        maxWidth: "60%",
                        height: "auto",
                        objectFit: "contain",
                        filter: "brightness(1.15) saturate(0.95)",
                    }}
                />
            </div>
            <h2
                className="mt-8"
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 400,
                    fontSize: "clamp(32px, 3.6vw, 44px)",
                    lineHeight: 1.2,
                    color: C.cream,
                }}
            >
                The door is open.
                <br />
                <em style={{ fontStyle: "italic" }}>Are you ready to step through?</em>
            </h2>
            <p
                className="mt-6"
                style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 17,
                    color: "rgba(245,240,232,0.96)",
                    lineHeight: 1.85,
                    marginBottom: 28,
                }}
            >
                A reading is a quiet, unhurried encounter with what the cards are asking of you
                — and what you are ready to hear. If you are curious, reach out. We will find
                the right way to begin.
            </p>
            <a
                href="/contact"
                data-testid="readings-cta-button"
                style={{
                    display: "inline-block",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                    background: C.gold,
                    color: C.midnight,
                    padding: "18px 42px",
                    borderRadius: 0,
                    textDecoration: "none",
                }}
            >
                Inquire About a Reading
            </a>
            <p
                style={{
                    marginTop: 22,
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: 16,
                    color: "rgba(245,240,232,0.92)",
                }}
            >
                Virtual and email readings available · Available worldwide · alchemypractice.com
            </p>
            <p
                style={{
                    marginTop: 20,
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: 20,
                    color: "rgba(245,240,232,0.96)",
                    lineHeight: 1.5,
                }}
            >
                The cards are waiting. So is the version of you that is ready to meet them.
            </p>
        </div>
    </section>
);

/* =====================================================================
   INTRO BLOCK — short elegant lead paragraph near the top of the page
   ===================================================================== */
const IntroBlock = () => (
    <section
        data-testid="readings-intro-block"
        className="relative"
        style={{ background: C.cream, padding: "72px 24px 40px" }}
    >
        <div className="mx-auto text-center" style={{ maxWidth: 720 }} data-reveal>
            <p
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: "clamp(22px, 2.4vw, 30px)",
                    lineHeight: 1.55,
                    color: C.charcoal,
                    fontWeight: 400,
                }}
            >
                The cards are available now. Each reading is held carefully — the same
                attentiveness Austine brings to her clinical work, in a space that lives
                outside of it.
            </p>
        </div>
    </section>
);

/* =====================================================================
   INLINE BOOK A READING CTA — smooth-scrolls to Ready-to-Begin section.
   Iter_15: color unified to BOOK_BTN_COLOR (berry) across the whole page.
   ===================================================================== */
const InlineBookCTA = ({ testid, label = "Book a Reading" }) => (
    <div style={{ background: C.cream, padding: "24px 24px 56px", textAlign: "center" }}>
        <a
            href="/book-a-reading"
            data-testid={testid}
            className="inline-flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-90"
            style={{
                background: BOOK_BTN_COLOR,
                color: "#FAFAF8",
                padding: "16px 32px",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                fontWeight: 500,
            }}
        >
            {label} <span aria-hidden>→</span>
        </a>
    </div>
);

/* =====================================================================
   MEET AUSTINE — bio section (above booking).
   Iter_15: Removed 3 tarot-spread placeholders per user request.
   Iter_15: Portrait now uses Austine's real headshot.
   ===================================================================== */
const MEET_PORTRAIT_PLACEHOLDER =
    "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/fwvbl4l9_Facetune_02-11-2025-15-31-34.jpeg";

const MeetAustineBio = () => (
    <section
        id="readings-meet-austine"
        data-testid="readings-meet-austine"
        className="relative overflow-hidden"
        style={{ background: C.ivory, padding: "88px 24px 96px" }}
    >
        <div className="mx-auto max-w-[1200px]" data-reveal>
            <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 lg:gap-20 items-start">
                {/* Portrait — Austine's headshot */}
                <div className="lg:sticky lg:top-24 self-start" data-testid="readings-meet-portrait">
                    <div
                        className="relative overflow-hidden"
                        style={{ border: `1px solid ${C.rosePurple}`, padding: 10, background: "#FFFFFF" }}
                    >
                        <img
                            src={MEET_PORTRAIT_PLACEHOLDER}
                            alt="Austine — tarot reader and licensed mental health therapist"
                            className="w-full h-[480px] object-cover object-top"
                            loading="lazy"
                        />
                        <span
                            className="absolute -top-3 -left-3 px-3 py-1 text-[10px] tracking-[0.3em] uppercase"
                            style={{ background: C.berry, color: "#FAFAF8", fontFamily: "'DM Sans', sans-serif" }}
                        >
                            Austine
                        </span>
                    </div>
                </div>

                <div className="space-y-6">
                    <span
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 12,
                            letterSpacing: "0.32em",
                            textTransform: "uppercase",
                            color: C.berry,
                            fontWeight: 500,
                        }}
                    >
                        Meet Austine
                    </span>
                    <h2
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: "clamp(32px, 3.6vw, 48px)",
                            lineHeight: 1.1,
                            color: C.tealDeep,
                            fontWeight: 500,
                        }}
                    >
                        In relationship with the cards{" "}
                        <em style={{ fontStyle: "italic", color: C.berry }}>since 1996.</em>
                    </h2>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17.5, lineHeight: 1.9, color: "rgba(44,40,37,0.88)" }}>
                        Austine has been in a relationship with the cards since 1996. Not always the right
                        kind — for a long time, like many people, she came to them looking for what she
                        wanted to see rather than what was true. But she kept returning, kept listening,
                        and in 2017 something shifted. During one of the harder chapters of her life, the
                        cards became a way back to herself — reflecting the feelings she hadn&rsquo;t yet
                        found words for, and helping her get in touch with an intuition she had been too
                        afraid to trust.
                    </p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17.5, lineHeight: 1.9, color: "rgba(44,40,37,0.88)" }}>
                        Austine is a licensed mental health therapist specializing in complex trauma. Her
                        clinical training has given her a deep ethical attentiveness — an understanding
                        that the people who come to the cards are often navigating something profoundly
                        real. A question is rarely just a question. She holds every reading with the same
                        care she brings to clinical work.
                    </p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17.5, lineHeight: 1.9, color: "rgba(44,40,37,0.88)" }}>
                        What she has found is that tarot and therapy can lead people to the same place —
                        a clearer sense of their values, a deeper trust in their own inner wisdom. The
                        path looks different. The destination often isn&rsquo;t.
                    </p>
                    <p
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 22,
                            lineHeight: 1.55,
                            color: C.tealDeep,
                            borderLeft: `3px solid ${C.rosePurple}`,
                            paddingLeft: 22,
                        }}
                    >
                        This is not therapy. It is not fortune telling. It is not a prediction about who
                        is coming back or what will happen next. It is a quiet, honest encounter with
                        where you are — and what that might be asking of you.
                    </p>
                </div>
            </div>
        </div>
    </section>
);

/* =====================================================================
   STICKY BOOK BUTTON — scrolls to the Ready-to-Begin booking section
   ===================================================================== */
const StickyBookButton = () => (
    <a
        href="/book-a-reading"
        data-testid="readings-sticky-book"
        className="fixed z-40 transition-transform duration-300 hover:-translate-y-1 hover:opacity-95"
        style={{
            right: 28,
            bottom: 28,
            background: BOOK_BTN_COLOR,
            color: "#FAFAF8",
            padding: "18px 30px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 500,
            boxShadow: "0 12px 32px rgba(122,45,80,0.34)",
        }}
    >
        Book a Reading
    </a>
);

/* =====================================================================
   PAGE
   ===================================================================== */
export default function AlchemyReadingsPage() {
    return (
        <main className="text-charcoal" data-testid="alchemy-readings-page" style={{ background: C.cream }}>
            <Seo
                title="The Reflection Deck | Tarot for People Who Want to Go Deeper | Alchemy Practice"
                description="A depth-oriented tarot reading service. Psychological depth, symbolic richness, and real questions — virtual and email readings available worldwide. By Austine Roney, Biddy Tarot certified."
            />
            <Nav />
            <StickyBookButton />
            <RevealOnScroll>
                <Hero />
                <IntroBlock />
                <InlineBookCTA testid="readings-inline-book-top" />
                <OrnateDivider bg={C.cream} />
                <IntroSplit />
                <Badge />
                <OrnateDivider bg={C.cream} />
                <Offerings />
                <InlineBookCTA testid="readings-inline-book-after-offerings" />
                <OrnateDivider bg={C.ivory} />
                <FAQ />
                <OrnateDivider bg={C.cream} />
                <Disclaimer />
                <MeetAustineBio />
                <InlineBookCTA testid="readings-inline-book-after-meet" />
                <ReadyToBegin />
                <CTA />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
